/**
 * BottomTab.js
 * "The Anchor" - Mobile primary navigation.
 */
import { BOTTOM_TABS } from '../../config/navigation.js';
import { renderIcon } from '../../utils/icons.js';
import { locale } from '../../services/LocaleService.js';

export class BottomTab {
    constructor(container) {
        this.container = container;
        this.render();
        this.setupRouterListener();
    }

    render() {
        const nav = document.createElement('nav');
        nav.className = "fixed bottom-0 left-0 right-0 h-[safe-area-inset-bottom+3.5rem] bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 flex justify-around items-start z-40 pb-[safe-area-inset-bottom] md:w-[400px] md:left-1/2 md:-translate-x-1/2 md:border-x md:rounded-t-2xl md:shadow-lg transition-transform duration-300";
        nav.id = "bottom-tab";

        BOTTOM_TABS.forEach(tab => {
            const btn = document.createElement('button');
            btn.className = "flex-1 flex flex-col items-center justify-center py-2 gap-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors relative group";
            btn.dataset.path = tab.path;

            // Icon Container
            const iconContainer = document.createElement('div');
            // We use a separate span for the icon to update it easily if needed, but renderIcon will inject <i>
            renderIcon(iconContainer, tab.icon, { class: "w-6 h-6 mb-0.5" });
            btn.appendChild(iconContainer);

            // Label
            const label = document.createElement('span');
            label.className = "text-[10px] font-medium";
            // [LOCALE]
            label.innerText = locale.t(`menu.${tab.id}`);
            btn.appendChild(label);

            // Active Indicator (Top Border)
            const indicator = document.createElement('div');
            indicator.className = "absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-violet-600 opacity-0 transition-opacity";
            btn.appendChild(indicator);

            // Click Handler
            btn.onclick = (e) => {
                e.preventDefault();
                if (window.router) {
                    window.router.navigate(tab.path);
                    this.updateActiveState(tab.path);
                }
            };

            nav.appendChild(btn);
        });

        this.container.appendChild(nav);
        this.navElement = nav;

        // Initial State
        this.updateActiveState(window.location.pathname);
    }

    updateActiveState(currentPath) {
        if (!this.navElement) return;

        const buttons = this.navElement.querySelectorAll('button');
        buttons.forEach(btn => {
            const path = btn.dataset.path;
            const isMatch = path === '/' ? currentPath === '/' : currentPath.startsWith(path);

            const label = btn.querySelector('span');
            const icon = btn.querySelector('i'); // Lucide svg or i
            const indicator = btn.querySelector('.absolute.top-0');

            if (isMatch) {
                btn.classList.remove('text-zinc-400');
                btn.classList.add('text-violet-600', 'dark:text-violet-500');
                if (indicator) indicator.classList.remove('opacity-0');
            } else {
                btn.classList.add('text-zinc-400');
                btn.classList.remove('text-violet-600', 'dark:text-violet-500');
                if (indicator) indicator.classList.add('opacity-0');
            }
        });
    }

    setupRouterListener() {
        // Listen to custom route change events if your router emits them
        window.addEventListener('popstate', () => {
            this.updateActiveState(window.location.pathname);
        });

        // Hook into window.router if possible, otherwise rely on popstate/click
        // Assuming router.navigate dispatches logic or update URL.
    }
}
